import { createContext, useContext, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { m, AnimatePresence } from 'framer-motion';

type ProgressState = 'idle' | 'loading' | 'completing';

const RouteProgressContext = createContext<{
  start: () => void;
  complete: () => void;
}>({ start: () => {}, complete: () => {} });

export function useRouteProgress() {
  return useContext(RouteProgressContext);
}

/**
 * How long a navigation has to take before the bar is drawn at all. A warm
 * page (its chunk preloaded, its data cached) renders on the tap's own commit,
 * and a bar that flashes in and fades out over 450ms for a page that is
 * already there reads as the app being slow. Only a navigation still pending
 * after this long gets one.
 */
export const PROGRESS_SHOW_DELAY_MS = 120;

/**
 * The top-of-screen bar for a route change.
 *
 * ORDER MATTERS, and it was wrong for months: the Router calls `start()` and
 * the page's PageTransition calls `complete()`. Effects run child-first, so
 * when both were layout effects — or `start` a passive one, as it was — a warm
 * navigation completed BEFORE it started, and the bar then ran to the 4s
 * safety net after every page that was already on screen. `start()` is now a
 * layout effect in the Router and `complete()` a passive effect in the page,
 * so complete always lands after start in the same commit.
 */
export function RouteProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ProgressState>('idle');
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    for (const ref of [idleTimerRef, safetyTimerRef, showTimerRef]) {
      if (ref.current) clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const complete = useCallback(() => {
    // Finished before the bar was ever shown: nothing to draw, nothing to fade.
    const neverShown = showTimerRef.current !== null;
    clearTimers();
    if (neverShown) {
      setState('idle');
      return;
    }
    setState(current => {
      // No-op if we were never loading — prevents a flash from double-complete calls
      if (current === 'idle') return 'idle';
      return 'completing';
    });
    idleTimerRef.current = setTimeout(() => setState('idle'), 450);
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setState('idle');

    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      setState('loading');
    }, PROGRESS_SHOW_DELAY_MS);

    // Safety net: if complete() is never called (e.g. redirect-only routes, errors),
    // auto-complete after 4 seconds so the bar never gets permanently stuck.
    safetyTimerRef.current = setTimeout(() => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
      setState(current => (current === 'loading' ? 'completing' : current));
      idleTimerRef.current = setTimeout(() => setState('idle'), 450);
    }, 4000);
  }, []);

  // One value for the life of the provider: every consumer (the Router, every
  // PageTransition) would otherwise re-render whenever the bar changed state.
  const value = useMemo(() => ({ start, complete }), [start, complete]);

  return (
    <RouteProgressContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {state !== 'idle' && (
          /* Offset by the notch inset — a 2px bar at top:0 is hidden behind the
             status bar in an installed PWA. */
          <m.div
            key="route-progress-bar"
            className="fixed top-[var(--sa-top)] left-0 right-0 z-toast h-[2px] origin-left pointer-events-none"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
            data-testid="route-progress"
            initial={{ scaleX: 0, opacity: 1 }}
            animate={
              state === 'loading'
                ? { scaleX: 0.82, opacity: 1 }
                : { scaleX: 1, opacity: 0 }
            }
            transition={
              state === 'loading'
                ? { duration: 0.9, ease: [0.4, 0, 0.2, 1] }
                : { scaleX: { duration: 0.18 }, opacity: { duration: 0.25, delay: 0.12 } }
            }
            exit={{ opacity: 0, transition: { duration: 0 } }}
          />
        )}
      </AnimatePresence>
    </RouteProgressContext.Provider>
  );
}
